/**
 * Sybotanica geo -> cart attribute bridge (+ SE/DK market redirect)
 * -----------------------------------------------------------------
 * Reads Cloudflare edge geo (/cdn-cgi/trace, `loc=<ISO>`) and writes the
 * detected country into the Shopify cart attribute `_geo_country` so that
 * server-rendered Liquid (snippets/nf-free-shipping-threshold.liquid,
 * snippets/nf-market-tiers.liquid) and assets/syba-cart-gift-engine.js can
 * show the correct per-country offers while the visitor stays on the English
 * primary market.
 *
 * EUROZONE markets (DE/FR/NL/BE/AT/ES/IT/PT/FI/IE/LU) share the primary
 * market's currency (EUR), so the visitor STAYS on the English root and the
 * offer content is rendered client-side from _geo_country (see the gift
 * engine) — no redirect, no extra subfolder, no SEO dilution.
 *
 * NON-EUR markets (SE -> SEK, DK -> DKK) genuinely need a currency switch,
 * which only a real Shopify market can do. So SE/DK visitors are redirected
 * ONCE to the published English-on-foreign-market subfolders /en-se/ and
 * /en-dk/, where currency, free-shipping threshold, gift threshold AND the
 * checkout shipping-country all resolve natively. (Per merchant decision
 * 2026-05-23. GB is Amazon-only and intentionally excluded.)
 *
 * Redirect discipline (Orbe-equivalent, best practice):
 *   - Override-safe: an explicit country choice (localStorage nf_geo_pref,
 *     set by the nf-geo-notice picker) always wins. pref=DE keeps an EU
 *     visitor on root; pref=SE/DK sends them to their subfolder.
 *   - Loop-safe: never fires when already on a localized /xx-xx/ subfolder
 *     (e.g. /en-se/, /sv-se/, /de-de/).
 *   - Bot-safe: skipped for crawler user-agents (bots crawl the canonical
 *     root — no cloaking).
 *   - History-safe: uses location.replace (no back-button bounce).
 *   - Preserves the full path + query + hash, so deep links and
 *     ?preview_theme_id survive the redirect.
 *
 * HARD RULE: never calls /localization. The ONLY URL change is the one-time
 * SE/DK subfolder redirect above; everything else leaves market/locale/URL
 * untouched. Idempotent per session via sessionStorage.
 */
(function () {
  'use strict';
  var SESSION_KEY = 'syba_geo_country_set'; // value = last detected ISO this session
  var ATTR = '_geo_country';
  var PREF_KEY = 'nf_geo_pref';
  var REDIR_KEY = 'syba_geo_redirected';

  // ISO -> English subfolder for the non-EUR markets that require a real
  // currency switch. EUR markets are intentionally absent (handled on root).
  var REDIRECT_MAP = { SE: '/en-se', DK: '/en-dk' };

  function parseLoc(text) {
    var m = (text || '').match(/(?:^|\n)loc=([A-Z]{2})/);
    return m ? m[1] : null;
  }

  function currentAttr() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { return (c && c.attributes && c.attributes[ATTR]) || ''; })
      .catch(function () { return ''; });
  }

  function setAttr(iso) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { _geo_country: iso } })
    });
  }

  // Publish the detected EUR-root country to the FAST client channels so the
  // gift engine can show the correct per-country shipping cost / threshold
  // WITHOUT waiting for the _geo_country cart-attribute round-trip:
  //   - localStorage cache: read synchronously on the next visit's first paint
  //     (so a returning visitor never sees the Ireland default flash).
  //   - window global + event: lets the gift engine (already loaded on this
  //     page) re-resolve the instant edge-geo returns on a first-ever visit.
  // Only call this for countries that STAY on the root (EUR) — SE/DK are
  // redirected to their own market subfolder before we get here, so they must
  // never be cached as the root offer country. Never throws.
  var CACHE_KEY = 'syba_geo_iso';
  function publishIso(iso) {
    if (!iso) return;
    iso = String(iso).toUpperCase();
    try { window.SybaDetectedCountry = iso; } catch (e) {}
    try { localStorage.setItem(CACHE_KEY, iso); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent('syba:geo-country', { detail: iso })); } catch (e) {}
  }

  // Long-lived explicit-country preference (set by nf-geo-notice when the
  // visitor commits a country). Its presence = "the user has decided" and is
  // the manual override for the auto-redirect.
  function validPref() {
    try {
      var p = JSON.parse(localStorage.getItem(PREF_KEY) || 'null');
      if (p && p.iso && /^[A-Z]{2}$/.test(p.iso) && p.exp && Date.now() < p.exp) return p;
    } catch (e) {}
    return null;
  }

  // True when the current URL is already a Shopify localized market subfolder
  // (e.g. /en-se/, /sv-se/, /de-de/). Prevents redirect loops and avoids
  // yanking a visitor who deliberately navigated into a localized market.
  function onLocalizedRoot() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root !== '/') {
        return true;
      }
    } catch (e) {}
    return /^\/[a-z]{2}-[a-z]{2}(\/|$)/i.test(location.pathname);
  }

  function isBotUA() {
    try {
      return /bot|crawl|spider|slurp|bing|google|yandex|baidu|duckduck|facebookexternalhit|embedly|quora|pinterest|slackbot|vkshare|whatsapp|telegram|discordbot|headless|lighthouse|gtmetrix|pingdom/i
        .test(navigator.userAgent || '');
    } catch (e) { return false; }
  }

  // Redirect SE/DK visitors to their English market subfolder. Returns true
  // if a redirect was triggered (caller must stop). All guards applied here.
  function maybeRedirect(iso) {
    if (!iso) return false;
    var target = REDIRECT_MAP[String(iso).toUpperCase()];
    if (!target) return false;            // EUR market or non-shippable: stay on root
    if (onLocalizedRoot()) return false;  // already in a market subfolder
    if (isBotUA()) return false;          // never cloak crawlers
    var dest = target + location.pathname + location.search + location.hash;
    try { sessionStorage.setItem(REDIR_KEY, target); } catch (e) {}
    // GA4 analytics: record the SE/DK auto-redirect. Pushed directly to
    // dataLayer (not via window.sybaGeoTrack) because this fires very early —
    // before nf-geo-notice defines the shared helper — and we're about to
    // navigate away. The GTM GA4 tag for syba_geo_redirect should use
    // transport_type:'beacon' so the hit survives the navigation.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'syba_geo_redirect',
        detected_country: String(iso).toUpperCase(),
        from_path: location.pathname,
        to_path: target,
        reason: 'sedk_currency'
      });
    } catch (e) { /* analytics must never block the redirect */ }
    try { location.replace(dest); } catch (e) { location.href = dest; }
    return true;
  }

  function run() {
    // ---- B2B exclusion (Task 6.15). Never write _geo_country / never
    // redirect on the B2B store, B2B pages, or for logged-in B2B customers.
    // Signals come from snippets/b2b-context.liquid (single source of truth).
    try {
      if (window.SybotanicaB2B && (window.SybotanicaB2B.isB2B || window.SybotanicaB2B.isB2BPage)) return;
    } catch (e) { /* SybotanicaB2B not defined yet: fall through to regex */ }
    if (/^\/(?:[a-z]{2}-[a-z]{2}\/)?(?:collections|pages)\/b2b[-_]/i.test(location.pathname)) return;

    // ---- QA override (no VPN needed): ?syba_force_geo=DK simulates the
    // Cloudflare edge-geo result, so you can test the auto-detect + SE/DK
    // redirect as if visiting from that country. Highest priority (overrides a
    // stored pref) and treated like a fresh detection — it does NOT write the
    // long-lived nf_geo_pref, so removing the param reverts to real behaviour.
    // The param survives the redirect (location.search is preserved) so it
    // keeps working on the destination subfolder. Harmless in production: it
    // only sets _geo_country, exactly what an explicit picker choice would.
    try {
      var fg = new URLSearchParams(location.search).get('syba_force_geo');
      if (fg && /^[A-Za-z]{2}$/.test(fg)) {
        var forced = fg.toUpperCase();
        if (maybeRedirect(forced)) return;
        publishIso(forced);
        setAttr(forced).then(function () {
          try { sessionStorage.setItem(SESSION_KEY, forced); } catch (e) {}
        }).catch(function () {});
        return;
      }
    } catch (e) { /* malformed query: ignore the override */ }

    // ---- Explicit-country preference path. A committed country drives BOTH
    // the redirect decision and the cart attribute, and skips geo-detection.
    var p = validPref();
    if (p) {
      // pref=SE/DK on the root → honor it by redirecting to the subfolder.
      // pref=EU country → maybeRedirect is a no-op (intentionally stay on root).
      if (maybeRedirect(p.iso)) return;
      publishIso(p.iso); // EUR country staying on root: fast-path the offer display

      // Slide the expiry forward another 365 days. Preserve the chosen reading
      // language (p.lang) — rebuilding the record without it would drop the
      // language the visitor picked in the orb (set by nf-geo-notice setPref).
      try {
        var slid = { iso: p.iso, exp: Date.now() + 365 * 864e5 };
        if (p.lang) { slid.lang = p.lang; }
        localStorage.setItem(PREF_KEY, JSON.stringify(slid));
      } catch (e) {}
      // Ensure the cart attribute equals the preferred ISO (once per session).
      var syncedThisSession = false;
      try { syncedThisSession = sessionStorage.getItem(SESSION_KEY) === p.iso; } catch (e) {}
      if (syncedThisSession) return;
      currentAttr().then(function (existing) {
        if (existing !== p.iso) {
          return setAttr(p.iso).then(function () { try { sessionStorage.setItem(SESSION_KEY, p.iso); } catch (e) {} });
        }
        try { sessionStorage.setItem(SESSION_KEY, p.iso); } catch (e) {}
      }).catch(function () { /* non-fatal: next load retries */ });
      return;
    }

    // ---- No explicit pref. Reuse this session's already-detected ISO if we
    // have it (stored as the SESSION_KEY value) — lets a within-session
    // return to the root still redirect SE/DK without re-hitting Cloudflare.
    var sessionIso = '';
    try { sessionIso = sessionStorage.getItem(SESSION_KEY) || ''; } catch (e) {}
    if (sessionIso) {
      if (maybeRedirect(sessionIso)) return; // SE/DK → subfolder; stop here
      publishIso(sessionIso);                // EUR: refresh the fast client channels
      return;                                // cart attribute already written this session
    }

    // ---- First detection this session: Cloudflare edge geo, then maybe
    // redirect (SE/DK), then write the cart attribute.
    fetch('/cdn-cgi/trace', { credentials: 'omit' })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var iso = parseLoc(text);
        if (!iso) return;
        if (maybeRedirect(iso)) return; // SE/DK → subfolder; stop here
        publishIso(iso); // EUR country on root: fast-path the offer display now
        return currentAttr().then(function (existing) {
          if (existing === iso) {
            try { sessionStorage.setItem(SESSION_KEY, iso); } catch (e) {}
            return;
          }
          return setAttr(iso).then(function () {
            try { sessionStorage.setItem(SESSION_KEY, iso); } catch (e) {}
          });
        });
      })
      .catch(function () { /* non-fatal: next load retries */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
